import { useQuery, useQueryInvalidate } from 'hooks';
import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { config } from 'data';
import { authService, profileService } from 'services';
import { Nullish } from 'types/common';
import { Profile, ProfileStatus } from 'types/models';
import { AuthEvent } from 'types/services';

type ContextValue = {
  initialized: boolean;
  authenticated: boolean;
  profile?: Nullish<Profile>;
  status?: Nullish<ProfileStatus>;
};

type ProviderProps = {
  children: ReactNode;
};

const AuthContext = createContext<ContextValue>({
  initialized: false,
  authenticated: false,
});

const AuthProvider: FC<ProviderProps> = ({ children }) => {
  const queryInvalidate = useQueryInvalidate();

  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [profile, setProfile] = useState<Nullish<Profile>>();
  const [status, setStatus] = useState<Nullish<ProfileStatus>>();

  useQuery({
    queryKey: [config.PROFILE_QUERY_KEY, 'auth'],
    queryFn: async () => {
      const profile = await profileService.getProfile();

      setInitialized(true);
      setProfile(profile);

      return profile;
    },
    enabled: authenticated,
    refetchInterval: config.AUTH_KEEP_ALIVE_INTERVAL,
    refetchIntervalInBackground: true,
    showMessage: false,
    showNotification: false,
  });

  const invalidateProfileQueries = useCallback(async () => {
    await queryInvalidate([config.PROFILE_QUERY_KEY]);
  }, [queryInvalidate]);

  const updateAuthenticatedState = useCallback(() => {
    const status = authService.getStatus();
    const token = authService.getAccessToken();
    const authenticated = Boolean(token);

    setInitialized(!authenticated);
    setAuthenticated(authenticated);
    setStatus(status);

    if (authenticated) {
      invalidateProfileQueries();
    }
  }, [invalidateProfileQueries]);

  useEffect(() => {
    updateAuthenticatedState();

    authService.on(AuthEvent.CREDENTIALS, updateAuthenticatedState);

    return () => {
      authService.off(AuthEvent.CREDENTIALS, updateAuthenticatedState);
    };
  }, [updateAuthenticatedState]);

  const value = useMemo<ContextValue>(() => ({
    initialized,
    authenticated,
    profile,
    status,
  }), [
    initialized,
    authenticated,
    profile,
    status,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const Auth = {
  Context: AuthContext,
  Provider: AuthProvider,
};

export default Auth;
